import { CRISP_WEBSITE_ID } from "@-/dev.improve.haniverse.lib.crisp";

const CRISP_SCRIPT_SRC = "https://client.crisp.chat/l.js";

type CrispGlobal = typeof globalThis & {
  $crisp?: unknown[];
  CRISP_WEBSITE_ID?: string;
};

function getCrispGlobal(): CrispGlobal {
  return globalThis;
}

/**
 * Injects Crisp's web widget loader into `<head>`, exactly as Crisp's copy-paste snippet does.
 *
 * Why this is done imperatively instead of rendering a `<script>` into the tree: React does not
 * execute `<script>` elements it renders as children, and this page is server-rendered — `document`
 * only exists once the client bundle runs. So the snippet has to be replayed from an effect.
 *
 * Idempotent: the widget is a global singleton keyed on `$crisp`, and re-adding the loader would
 * boot a second chatbox. Both a repeat mount (React 19 strict-mode double effects, or a user
 * navigating away and back) and a second call are no-ops.
 */
export function loadCrispChatWidget(): void {
  if (typeof document === "undefined") {
    return;
  }

  const crispGlobal = getCrispGlobal();

  if (document.querySelector(`script[src="${CRISP_SCRIPT_SRC}"]`) != null) {
    return;
  }

  crispGlobal.$crisp = crispGlobal.$crisp ?? [];
  crispGlobal.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;

  const script = document.createElement("script");
  script.src = CRISP_SCRIPT_SRC;
  script.async = true;
  document.head.append(script);
}

/**
 * Opens the chat window. Crisp replays commands queued on `$crisp` once its script loads, so this
 * also works while the widget is still loading.
 */
export function openCrispChat(): void {
  const crispGlobal = getCrispGlobal();
  crispGlobal.$crisp = crispGlobal.$crisp ?? [];
  crispGlobal.$crisp.push(["do", "chat:open"]);
}
